import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { useApi } from '../hooks/useApi.js';
import { notificationApi } from '../api/endpoints.js';
import { Avatar, Badge } from './ui.jsx';
import { fmt } from '../utils/format.js';

/**
 * Navigation is declared with the permission each item needs, so the sidebar
 * shows exactly what the signed-in user can actually open — no dead links.
 */
const NAV = [
  {
    section: null,
    items: [{ to: '/', label: 'Dashboard', icon: '📊', end: true }],
  },
  {
    section: 'People',
    items: [
      { to: '/employees', label: 'Employees', icon: '👥', anyOf: ['employee.view.all', 'employee.view.team'] },
      { to: '/org-chart', label: 'Org chart', icon: '🌳' },
      { to: '/employees/invites', label: 'Invite employees', icon: '✉️', permission: 'employee.invite' },
      { to: '/resignations', label: 'Exit requests', icon: '🚪', anyOf: ['resignation.view.team', 'resignation.view.all'] },
    ],
  },
  {
    section: 'Attendance',
    items: [
      { to: '/attendance', label: 'Daily register', icon: '🕒', anyOf: ['attendance.view.all', 'attendance.view.team'] },
      { to: '/attendance/regularizations', label: 'Regularizations', icon: '📝', anyOf: ['attendance.regularize.approve', 'attendance.view.all'] },
      { to: '/attendance/wfh', label: 'Work from home', icon: '🏠', anyOf: ['wfh.view.all', 'wfh.view.team'] },
      { to: '/attendance/tasks', label: 'Daily tasks', icon: '📋', anyOf: ['task.view.all', 'task.view.team'] },
      { to: '/attendance/devices', label: 'Punch devices', icon: '📟', permission: 'attendance.device.manage' },
    ],
  },
  {
    section: 'Time off',
    items: [
      { to: '/leave', label: 'Leave requests', icon: '🌴', anyOf: ['leave.view.all', 'leave.view.team'] },
      { to: '/leave/balances', label: 'Leave balances', icon: '⚖️', permission: 'leave.view.all' },
      { to: '/holidays', label: 'Holidays', icon: '🎉' },
    ],
  },
  {
    section: 'Payroll',
    items: [
      { to: '/payroll', label: 'Payroll runs', icon: '💰', permission: 'payroll.view.all' },
      { to: '/payroll/structures', label: 'Salary structures', icon: '📑', permission: 'payroll.structure.manage' },
      { to: '/payroll/payslips', label: 'Payslips', icon: '🧾', permission: 'payroll.view.all' },
      { to: '/expenses', label: 'Expense claims', icon: '💳', anyOf: ['expense.view.all', 'expense.view.team'] },
    ],
  },
  {
    section: 'Documents',
    items: [
      { to: '/documents', label: 'Documents', icon: '📁', anyOf: ['document.manage', 'document.view.all'] },
      { to: '/documents/form16', label: 'Form 16', icon: '📄', permission: 'document.manage' },
    ],
  },
  {
    section: 'Admin',
    items: [
      { to: '/reports', label: 'Reports', icon: '📈', permission: 'report.view' },
      { to: '/settings', label: 'Settings', icon: '⚙️', permission: 'settings.manage' },
      { to: '/audit', label: 'Audit log', icon: '🔍', permission: 'audit.view' },
    ],
  },
];

export default function Layout() {
  const { user, employee, signOut, can, canAny } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { data: unread, reload: reloadUnread } = useApi(() => notificationApi.unreadCount(), []);

  // Navigating on a phone should close the drawer behind you.
  useEffect(() => {
    setSidebarOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickAway = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, []);

  // Keep the badge roughly current without a websocket.
  useEffect(() => {
    const timer = setInterval(reloadUnread, 60_000);
    return () => clearInterval(timer);
  }, [reloadUnread]);

  const visible = (item) => {
    if (item.permission) return can(item.permission);
    if (item.anyOf) return canAny(...item.anyOf);
    return true;
  };

  const sections = NAV
    .map((group) => ({ ...group, items: group.items.filter(visible) }))
    .filter((group) => group.items.length > 0);

  return (
    <div className="layout">
      <aside className={`sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="sidebar__brand">
          <span className="sidebar__logo" aria-hidden="true">CH</span>
          <span className="sidebar__name">Catchu HRMS</span>
        </div>

        <nav className="sidebar__nav">
          {sections.map((group) => (
            <div key={group.section ?? 'main'} className="sidebar__group">
              {group.section && <p className="sidebar__section">{group.section}</p>}
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => `sidebar__link ${isActive ? 'is-active' : ''}`}
                >
                  <span className="sidebar__icon" aria-hidden="true">{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar__footer">
          <span className="sidebar__version">v1.0.0</span>
        </div>
      </aside>

      {sidebarOpen && <div className="sidebar-scrim" onClick={() => setSidebarOpen(false)} />}

      <div className="layout__main">
        <header className="topbar">
          <button
            type="button"
            className="topbar__burger"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            ☰
          </button>

          <div className="topbar__spacer" />

          <button
            type="button"
            className="topbar__icon-btn"
            onClick={() => navigate('/notifications')}
            aria-label={`Notifications${unread?.unread ? `, ${unread.unread} unread` : ''}`}
          >
            🔔
            {unread?.unread > 0 && <span className="topbar__badge">{unread.unread > 99 ? '99+' : unread.unread}</span>}
          </button>

          <div className="topbar__user" ref={menuRef}>
            <button type="button" className="topbar__user-btn" onClick={() => setMenuOpen((v) => !v)}>
              <Avatar name={user?.name} src={user?.avatarUrl} size={32} />
              <span className="topbar__user-meta">
                <span className="topbar__user-name">{user?.name}</span>
                <span className="topbar__user-role">{fmt.label(user?.role)}</span>
              </span>
              <span aria-hidden="true">▾</span>
            </button>

            {menuOpen && (
              <div className="menu">
                <div className="menu__header">
                  <strong>{user?.name}</strong>
                  <small>{user?.email}</small>
                  {employee?.employeeCode && <Badge tone="info">{employee.employeeCode}</Badge>}
                </div>
                <button type="button" className="menu__item" onClick={() => navigate('/profile')}>
                  My profile
                </button>
                <button type="button" className="menu__item" onClick={() => navigate('/profile#password')}>
                  Change password
                </button>
                <hr />
                <button
                  type="button"
                  className="menu__item menu__item--danger"
                  onClick={async () => {
                    await signOut();
                    navigate('/login', { replace: true });
                  }}
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </header>

        <main className="content">
          <Outlet context={{ reloadUnread }} />
        </main>
      </div>
    </div>
  );
}
